import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, DrawerBody } from '@fluentui/react-components';
import {
    ArrowClockwise16Regular,
    Copy16Regular,
    Dismiss20Regular,
    Mail16Regular,
} from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import { GenerateShareUpdate, EmailShareUpdate } from '../../redux/taskSlice';
import {
    formatShareUpdateAsSelected,
    buildDefaultEmailSubject,
    htmlToPlainText,
    isShareUpdateEmpty,
} from '../../utils/formatShareUpdateAsSelected';
import styles from './css/shareUpdateSidebar.module.css';

const MODES = {
    AI: 'ai_summary',
    AS_SELECTED: 'as_selected',
};

const EDITOR_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};

const EDITOR_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

const copyRichHtml = async html => {
    const plain = htmlToPlainText(html);
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plain], { type: 'text/plain' }),
            }),
        ]);
        return;
    }
    await navigator.clipboard.writeText(plain);
};

const ShareUpdateSidebar = ({
    open,
    onClose,
    selectedRows = [],
    taskData = [],
    selectedProject,
    projectUsersAll = [],
    customOrder = null,
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);
    const { shareUpdateGenerateLoading, shareUpdateEmailLoading } = useSelector(state => state.task);

    const [mode, setMode] = React.useState(MODES.AI);
    const [purpose, setPurpose] = React.useState('');
    const [aiDraft, setAiDraft] = React.useState('');
    const [asSelectedDraft, setAsSelectedDraft] = React.useState('');
    const [panelView, setPanelView] = React.useState('editor');
    const [emailSubject, setEmailSubject] = React.useState('');
    const generateRequestRef = React.useRef(0);

    const editorHtml = mode === MODES.AI ? aiDraft : asSelectedDraft;
    const taskCount = selectedRows?.length || 0;
    const editorEmpty = isShareUpdateEmpty(editorHtml);
    const aiGenerating = shareUpdateGenerateLoading && mode === MODES.AI;

    const generateAsSelected = React.useCallback(() => {
        const html = formatShareUpdateAsSelected({
            tasks: taskData,
            selectedIds: selectedRows,
            customOrder,
            selectedProject,
            projectUsersAll,
        });
        setAsSelectedDraft(html);
    }, [taskData, selectedRows, customOrder, selectedProject, projectUsersAll]);

    const generateAiSummary = React.useCallback(async () => {
        const requestId = ++generateRequestRef.current;
        try {
            const result = await dispatch(
                GenerateShareUpdate({
                    taskIds: selectedRows,
                    purpose: purpose.trim(),
                }),
            ).unwrap();
            if (requestId !== generateRequestRef.current) return;
            setAiDraft(result?.text || '');
        } catch (error) {
            if (requestId !== generateRequestRef.current) return;
            toast.error(typeof error === 'string' ? error : 'Failed to generate AI summary');
        }
    }, [dispatch, selectedRows, purpose]);

    const handleModeChange = newMode => {
        if (newMode === mode) return;
        setMode(newMode);
        if (newMode === MODES.AS_SELECTED) {
            generateAsSelected();
            return;
        }
        if (!aiDraft && !shareUpdateGenerateLoading) {
            generateAiSummary();
        }
    };

    const handleRegenerate = () => {
        if (mode === MODES.AI) {
            generateAiSummary();
        } else {
            generateAsSelected();
        }
    };

    const handleEditorChange = (html, _delta, source) => {
        if (source !== 'user') return;
        if (mode === MODES.AI) {
            setAiDraft(html);
        } else {
            setAsSelectedDraft(html);
        }
    };

    const handleCopy = async () => {
        if (editorEmpty) {
            toast.error('Nothing to copy');
            return;
        }
        try {
            await copyRichHtml(editorHtml);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Failed to copy text');
        }
    };

    const handleOpenEmailConfirm = () => {
        if (editorEmpty) {
            toast.error('Nothing to email');
            return;
        }
        setEmailSubject(buildDefaultEmailSubject(selectedProject?.project_name));
        setPanelView('emailConfirm');
    };

    const handleSendEmail = async () => {
        try {
            await dispatch(
                EmailShareUpdate({
                    subject: emailSubject,
                    body: editorHtml,
                }),
            ).unwrap();
            setPanelView('editor');
        } catch {
            // toast handled in slice
        }
    };

    const handleClose = () => {
        setPanelView('editor');
        setPurpose('');
        setAiDraft('');
        setAsSelectedDraft('');
        setMode(MODES.AI);
        generateRequestRef.current += 1;
        onClose?.();
    };

    React.useEffect(() => {
        if (!open || !selectedRows?.length) return;
        setPanelView('editor');
        setMode(MODES.AI);
        setPurpose('');
        setAiDraft('');
        setAsSelectedDraft('');
        generateAiSummary();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderEditorView = () => (
        <>
            <div className={styles.configSection}>
                <p className={styles.sectionLabel}>How should these tasks be presented?</p>
                <div className={styles.segmented}>
                    <button
                        type="button"
                        className={`${styles.segment} ${mode === MODES.AI ? styles.segmentActive : ''}`}
                        onClick={() => handleModeChange(MODES.AI)}>
                        AI summary
                    </button>
                    <button
                        type="button"
                        className={`${styles.segment} ${mode === MODES.AS_SELECTED ? styles.segmentActive : ''}`}
                        onClick={() => handleModeChange(MODES.AS_SELECTED)}>
                        As selected
                    </button>
                </div>

                {mode === MODES.AI && (
                    <div className={styles.purposeField}>
                        <p className={styles.sectionLabel}>What is this update for?</p>
                        <input
                            type="text"
                            className={styles.purposeInput}
                            placeholder="e.g. CXO update on priorities for this week"
                            value={purpose}
                            onChange={e => setPurpose(e.target.value)}
                        />
                        <p className={styles.optionalHint}>Optional</p>
                    </div>
                )}
            </div>

            <div className={styles.contentSection}>
                <p className={styles.updateLabel}>Update</p>
                <div className={styles.editorWrapper}>
                    <ReactQuill
                        key={mode}
                        className={styles.richEditor}
                        theme="snow"
                        value={editorHtml}
                        onChange={handleEditorChange}
                        modules={EDITOR_MODULES}
                        formats={EDITOR_FORMATS}
                        readOnly={aiGenerating}
                        placeholder={
                            aiGenerating ? 'Generating update…' : 'Your update will appear here'
                        }
                    />
                </div>
                {mode === MODES.AI && (
                    <button
                        type="button"
                        className={styles.regenerateButton}
                        onClick={handleRegenerate}
                        disabled={shareUpdateGenerateLoading}>
                        <ArrowClockwise16Regular />
                        Regenerate
                    </button>
                )}
            </div>

            <div className={styles.footer}>
                <button type="button" className={styles.copyButton} onClick={handleCopy} disabled={editorEmpty}>
                    <Copy16Regular />
                    Copy
                </button>
                <button
                    type="button"
                    className={styles.emailButton}
                    onClick={handleOpenEmailConfirm}
                    disabled={editorEmpty || shareUpdateEmailLoading}>
                    <Mail16Regular />
                    Email to me
                </button>
            </div>
        </>
    );

    const renderEmailConfirmView = () => (
        <>
            <div className={styles.emailConfirmSection}>
                <p className={styles.emailConfirmTitle}>Email this update to yourself?</p>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>To</p>
                    <div className={styles.readOnlyField}>{user?.email || '—'}</div>
                </div>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>Subject</p>
                    <input
                        type="text"
                        className={styles.subjectInput}
                        value={emailSubject}
                        onChange={e => setEmailSubject(e.target.value)}
                    />
                </div>

                <div className={styles.fieldGroup}>
                    <p className={styles.fieldLabel}>Message</p>
                    <div className={styles.messageNote}>
                        The current contents of the Share Update editor will be included as the message body.
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <button type="button" className={styles.cancelButton} onClick={() => setPanelView('editor')}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.emailButton}
                    onClick={handleSendEmail}
                    disabled={shareUpdateEmailLoading}>
                    <Mail16Regular />
                    {shareUpdateEmailLoading ? 'Sending…' : 'Send to me'}
                </button>
            </div>
        </>
    );

    return (
        <Drawer
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(_, data) => {
                if (!data.open) handleClose();
            }}
            size="medium"
            style={{ maxWidth: 472 }}>
            <DrawerBody style={{ padding: 0, height: '100%', overflow: 'hidden' }}>
                <div className={styles.panel}>
                    <div className={styles.header}>
                        <div className={styles.headerTop}>
                            <div>
                                <h2 className={styles.title}>Share Update</h2>
                                <p className={styles.subtitle}>
                                    {taskCount} task{taskCount === 1 ? '' : 's'} selected
                                </p>
                            </div>
                            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
                                <Dismiss20Regular />
                            </button>
                        </div>
                    </div>

                    {panelView === 'emailConfirm' ? renderEmailConfirmView() : renderEditorView()}
                </div>
            </DrawerBody>
        </Drawer>
    );
};

export default ShareUpdateSidebar;
